/**
 * Recharge cart drawer — effective saving % fix.
 *
 * Recharge Storefront Experiences renders each line's "Saving X%" badge from the
 * static subscription-plan discount and ignores stacked Shopify discount codes
 * (e.g. TEAMTNP). The prices it shows are already correct, so we recompute the
 * badge from the displayed original vs. final price:
 *   pct = (original - final) / original * 100
 *
 * Lives inside the Recharge widget's (open) Shadow DOM, so we attach a
 * MutationObserver that self-converges (writes only on a real change and ignores
 * its own writes — it disconnects while writing, so it never loops). It does not
 * try to suppress Recharge's own whole-line re-render; it only corrects the value.
 */
(() => {
  if (window.__tnpRechargeSavingsFix) return;
  window.__tnpRechargeSavingsFix = true;

  const HOST_SELECTOR = '#recharge-storefront-experiences__cart-drawer';
  const READY_INTERVAL = 250;
  const READY_TIMEOUT = 15000;
  const OBSERVE_OPTS = { childList: true, subtree: true, characterData: true };

  /** Parse a localized money string to Number; handles thousands separators. */
  const parseMoney = (text) => {
    const cleaned = (text || '').replace(/[^\d.,]/g, '');
    if (!cleaned) return NaN;
    // Right-most separator is the decimal one; the rest are thousands separators.
    const lastSep = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    if (lastSep === -1) return parseFloat(cleaned);
    const intPart = cleaned.slice(0, lastSep).replace(/[.,]/g, '');
    return parseFloat(`${intPart}.${cleaned.slice(lastSep + 1)}`);
  };

  /** Resolve the open shadow root of the Recharge drawer (host or first child). */
  const getShadowRoot = () => {
    const host = document.querySelector(HOST_SELECTOR);
    if (!host) return null;
    return host.shadowRoot || host.firstElementChild?.shadowRoot || null;
  };

  /** Recompute and rewrite the saving badge of every discounted line item. */
  const updateBadges = (root) => {
    root.querySelectorAll('[role="listitem"]').forEach((item) => {
      const discountedEl = item.querySelector('[part~="discount"]');
      if (!discountedEl) return; // no per-line discount → nothing to fix

      const priceCol = discountedEl.parentElement;
      if (!priceCol) return;

      // `part="money discount"` matches both, so the "original" is the other money span.
      const originalEl = [...priceCol.querySelectorAll('[part~="money"]')]
        .find((el) => el !== discountedEl);
      // The badge is the element in the price column that shows a percentage.
      const badgeEl = [...priceCol.children]
        .find((el) => el !== discountedEl && /\d\s*%/.test(el.textContent));
      if (!originalEl || !badgeEl) return;

      const original = parseMoney(originalEl.textContent);
      const final = parseMoney(discountedEl.textContent);
      if (!(original > 0) || !(final >= 0) || final >= original) return;

      const pct = Math.round(((original - final) / original) * 100);
      // Keep the existing wording/locale, replace only the number ("Saving 25%" → "Saving 70%").
      const next = /\d[\d.,]*\s*%/.test(badgeEl.textContent)
        ? badgeEl.textContent.replace(/\d[\d.,]*\s*%/, `${pct}%`)
        : `Saving ${pct}%`;

      if (badgeEl.textContent !== next) badgeEl.textContent = next;
    });
  };

  /** Self-converging observer: disconnect while we write so we never loop. */
  const attach = (root) => {
    const observer = new MutationObserver(() => {
      observer.disconnect();
      try {
        updateBadges(root);
      } finally {
        observer.observe(root, OBSERVE_OPTS);
      }
    });
    observer.observe(root, OBSERVE_OPTS);
    updateBadges(root); // initial pass for an already-open drawer
  };

  /** Wait for the Recharge widget to mount, then attach. */
  const start = () => {
    const root = getShadowRoot();
    if (root) {
      attach(root);
      return;
    }
    let waited = 0;
    const timer = setInterval(() => {
      const r = getShadowRoot();
      waited += READY_INTERVAL;
      if (r) {
        clearInterval(timer);
        attach(r);
      } else if (waited >= READY_TIMEOUT) {
        clearInterval(timer);
      }
    }, READY_INTERVAL);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
